import { useState, useRef, useEffect } from "react";
import { Sparkles, Send, X, Minus, Check, Zap, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { API, getToken } from "@/lib/api";
import api from "@/lib/api";

function renderMd(text) {
  const parts = text.split(/```/);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      const lines = part.split("\n");
      let info = lines[0].trim();
      let code = part;
      if (info && !info.includes(" ") && info.length < 40) code = lines.slice(1).join("\n");
      return (
        <pre key={i} className="text-xs"><code>{code.replace(/\n$/, "")}</code></pre>
      );
    }
    const html = part
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/`([^`]+)`/g, "<code>$1</code>")
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/^### (.*)$/gm, "<h3>$1</h3>")
      .replace(/^## (.*)$/gm, "<h3>$1</h3>")
      .replace(/^- (.*)$/gm, "• $1")
      .replace(/\n/g, "<br/>");
    return <span key={i} className="md" dangerouslySetInnerHTML={{ __html: html }} />;
  });
}

function parseFiles(text) {
  const files = [];
  const parts = text.split(/```/);
  for (let i = 1; i < parts.length; i += 2) {
    const block = parts[i];
    const lines = block.split("\n");
    const info = lines[0].trim();
    if (info && info.includes(".") && !info.includes(" ")) {
      files.push({ name: info, content: lines.slice(1).join("\n").replace(/\n$/, "") });
    }
  }
  return files;
}

export default function AIPanel({ context, onApplyFiles, seedPrompt, onClose, onMinimize, credits: creditsProp, mode = "coding", sessionId: sessionIdProp }) {
  const isCourse = mode === "course";
  const welcome = isCourse
    ? { role: "assistant", content: "Hi! I'm your **KiqHub Course Tutor** 📚\n\nAsk me to explain any concept from this lesson, search a topic, or clarify something you're stuck on. I'm free and unlimited — but I only teach, I don't write or fix code." }
    : { role: "assistant", content: "Hi! I'm **KiqHub AI** ✨\n\nI can build whole projects, generate files, explain code, suggest libraries and fix errors — line by line. What are we building?" };
  const [messages, setMessages] = useState([welcome]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [credits, setCredits] = useState(creditsProp || null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const sessionId = useRef(sessionIdProp || Math.random().toString(36).slice(2));
  const scrollRef = useRef();

  const loadCredits = async () => {
    if (isCourse) return;
    try { const { data } = await api.get("/ai/credits"); setCredits(data); } catch {}
  };
  // Load permanent, scrollable chat history for this session
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/ai/history?session_id=${encodeURIComponent(sessionId.current)}`);
        if (data && data.length) setMessages([welcome, ...data.map((m) => ({ role: m.role, content: m.content }))]);
      } catch {}
    })();
    loadCredits();
    // eslint-disable-next-line
  }, []);
  useEffect(() => { if (seedPrompt) send(seedPrompt); /* eslint-disable-next-line */ }, [seedPrompt]);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ session_id: sessionId.current, message: msg, context: context || null, mode }),
      });
      if (res.status === 402) {
        const err = await res.json();
        let parsed; try { parsed = JSON.parse(err.detail); } catch { parsed = { message: err.detail }; }
        setMessages((m) => { const c = [...m]; c[c.length - 1].content = parsed.message; return c; });
        setShowUpgrade(true);
        setBusy(false);
        return;
      }
      if (!res.ok) throw new Error("AI request failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1].content = acc; return c; });
      }
      loadCredits();
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1].content = "⚠️ " + e.message; return c; });
    } finally { setBusy(false); }
  };

  const upgrade = async (plan) => {
    const { data } = await api.post("/ai/upgrade", { plan });
    toast.info(data.message, { duration: 6000 });
  };

  const suggestions = isCourse
    ? ["Explain today's topic simply", "Give me a real-world example", "Why does this matter?"]
    : ["Create a todo app in HTML/CSS/JS", "Explain this file", "Fix the error in my code"];

  return (
    <div className="h-full flex flex-col glass-strong" data-testid="ai-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
        <div className="flex items-center gap-2 text-teal-accent font-semibold text-sm">
          <Sparkles size={16} /> {isCourse ? "KIQHUB TUTOR" : "KIQHUB AI"}
        </div>
        <div className="flex items-center gap-3">
          {isCourse ? (
            <span className="text-xs pill px-3 py-1 bg-[#00D4D4]/10 text-teal-accent flex items-center gap-1"><Zap size={12} /> Free</span>
          ) : credits && (
            <button onClick={() => setShowUpgrade(true)} data-testid="ai-credits-badge"
                    className={`text-xs pill px-3 py-1 flex items-center gap-1 ${credits.low ? "bg-amber-500/15 text-amber-400" : "bg-[#00D4D4]/10 text-teal-accent"}`}>
              <Zap size={12} /> {credits.total_remaining} credits
            </button>
          )}
          {onMinimize && <button data-testid="ai-minimize" onClick={onMinimize} className="text-muted-foreground hover:text-foreground"><Minus size={18} /></button>}
          {onClose && <button data-testid="ai-close" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4 text-sm">
        {!isCourse && credits && credits.low && credits.total_remaining > 0 && (
          <div data-testid="low-credit-warning" className="glass rounded-xl px-4 py-3 border border-amber-400/30 flex items-center justify-between">
            <span className="text-xs text-amber-400">Your credits are low. Please upgrade.</span>
            <button onClick={() => setShowUpgrade(true)} className="pill px-3 py-1 text-xs bg-[#00D4D4]/15 text-teal-accent">Upgrade Credits</button>
          </div>
        )}
        {messages.map((m, i) => {
          const files = (!isCourse && m.role === "assistant") ? parseFiles(m.content) : [];
          return (
            <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
              <div className={`rounded-2xl px-4 py-3 max-w-[92%] ${m.role === "user" ? "bg-[#00D4D4]/15 text-foreground" : "glass"}`}>
                <div className="leading-relaxed break-words">{m.content ? renderMd(m.content) : <span className="text-muted-foreground animate-pulse">thinking…</span>}</div>
                {files.length > 0 && onApplyFiles && (
                  <button data-testid="ai-allow-apply" onClick={() => { onApplyFiles(files); toast.success(`Applied ${files.length} file(s)`); }}
                          className="mt-3 kiq-btn px-4 py-2 text-xs flex items-center gap-2">
                    <Check size={14} /> Allow & apply {files.length} file{files.length > 1 ? "s" : ""}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-3 pb-3 pt-2 border-t border-border/60">
        <div className="flex flex-wrap gap-2 mb-2">
          {suggestions.map((s) => (
            <button key={s} onClick={() => send(s)} disabled={busy}
                    className="text-xs pill px-3 py-1.5 glass hover:border-[#00D4D4]/50 text-muted-foreground">
              {s}
            </button>
          ))}
        </div>
        <div className="kiq-input flex items-end gap-2 px-3 py-2">
          {!isCourse && (
            <label className="cursor-pointer text-muted-foreground hover:text-teal-accent pb-1">
              <Paperclip size={16} />
              <input type="file" className="hidden" onChange={() => toast.info("Attachment noted — describe what you'd like me to do with it.")} />
            </label>
          )}
          <textarea data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} rows={3}
                 onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                 placeholder={isCourse ? "Ask about any concept in this lesson…" : "Ask AI to build, explain or fix…"}
                 className="bg-transparent outline-none flex-1 text-sm placeholder:text-muted-foreground/60 resize-none leading-relaxed" />
          <button data-testid="ai-send" onClick={() => send()} disabled={busy}
                  className="text-teal-accent disabled:opacity-40 pb-1"><Send size={18} /></button>
        </div>
      </div>

      {showUpgrade && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowUpgrade(false)}>
          <div className="glass-strong rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="upgrade-modal">
            <h3 className="serif text-2xl mb-1">Upgrade Credits</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {credits ? `You have ${credits.total_remaining} credits left. ` : ""}Purchased credits never expire.
            </p>
            {(credits?.intro_available ? [{ price: 49, pts: 250, intro: true }] : []).concat([
              { price: 99, pts: 250 }, { price: 149, pts: 400 }, { price: 199, pts: 500 },
            ]).map((p) => (
              <div key={p.price} className={`glass rounded-2xl p-4 flex items-center justify-between mb-3 ${p.intro ? "border border-[#00D4D4]/40" : ""}`}>
                <div>
                  <div className="font-semibold flex items-center gap-2">{p.pts} Credits {p.intro && <span className="pill px-2 py-0.5 text-[10px] bg-[#00D4D4]/20 text-teal-accent">FIRST-TIME OFFER</span>}</div>
                  <div className="text-xs text-muted-foreground">Never expires</div>
                </div>
                <button data-testid={`upgrade-${p.price}`} onClick={() => upgrade(String(p.price))} className="kiq-btn px-5 py-2 text-sm">₹{p.price}</button>
              </div>
            ))}
            <button onClick={() => setShowUpgrade(false)} className="w-full text-sm text-muted-foreground mt-1">Maybe later</button>
          </div>
        </div>
      )}
    </div>
  );
}
